import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X, Search } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../ui/Toast';
import type { FSReposting, LineConfig } from '../../types/financialStatement';

interface Props {
    company: string;
    year: number;
    canEdit: boolean;
    onRepostingsChanged: () => void;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
}

interface RepostingForm {
    debitLineNr: number | null;
    creditLineNr: number | null;
    amount: string;
    reason: string;
}

const emptyForm: RepostingForm = { debitLineNr: null, creditLineNr: null, amount: '', reason: '' };

export function RepostingsTab({ company, year, canEdit, onRepostingsChanged }: Props) {
    const { toast } = useToast();
    const [repostings, setRepostings] = useState<FSReposting[]>([]);
    const [lines, setLines] = useState<LineConfig[]>([]);
    const [loading, setLoading] = useState(true);

    // Add/Edit state
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<RepostingForm>(emptyForm);
    const [saving, setSaving] = useState(false);

    // Delete state
    const [deleteTarget, setDeleteTarget] = useState<FSReposting | null>(null);

    // Line search filter
    const [debitSearch, setDebitSearch] = useState('');
    const [creditSearch, setCreditSearch] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [repostingResult, lineResult] = await Promise.allSettled([
                financialStatementApi.getRepostings({ company, year }),
                financialStatementApi.getLinesConfig(),
            ]);
            if (repostingResult.status === 'fulfilled') {
                setRepostings(repostingResult.value.repostings ?? []);
            } else {
                console.error('Failed to load repostings:', repostingResult.reason);
                toast('Failed to load repostings', 'error');
            }
            if (lineResult.status === 'fulfilled') {
                setLines(lineResult.value.lines);
            } else {
                console.error('Failed to load lines:', lineResult.reason);
            }
        } finally {
            setLoading(false);
        }
    }, [company, year, toast]);

    useEffect(() => { loadData(); }, [loadData]);

    const dataLines = useMemo(() =>
        [...lines].sort((a, b) => a.LineNr - b.LineNr),
        [lines]
    );

    const filteredDebitLines = useMemo(() => {
        if (!debitSearch) return dataLines;
        const q = debitSearch.toLowerCase();
        return dataLines.filter(l =>
            String(l.LineNr).includes(q) ||
            l.LineNameEnglish.toLowerCase().includes(q) ||
            l.LineNameGerman.toLowerCase().includes(q)
        );
    }, [dataLines, debitSearch]);

    const filteredCreditLines = useMemo(() => {
        if (!creditSearch) return dataLines;
        const q = creditSearch.toLowerCase();
        return dataLines.filter(l =>
            String(l.LineNr).includes(q) ||
            l.LineNameEnglish.toLowerCase().includes(q) ||
            l.LineNameGerman.toLowerCase().includes(q)
        );
    }, [dataLines, creditSearch]);

    const startAdd = () => {
        setIsAdding(true);
        setEditingId(null);
        setForm(emptyForm);
        setDebitSearch('');
        setCreditSearch('');
    };

    const startEdit = (r: FSReposting) => {
        setEditingId(r.RepostingId);
        setIsAdding(false);
        setForm({
            debitLineNr: r.DebitLineNr,
            creditLineNr: r.CreditLineNr,
            amount: String(r.Amount),
            reason: r.Reason,
        });
        setDebitSearch('');
        setCreditSearch('');
    };

    const cancelForm = () => {
        setIsAdding(false);
        setEditingId(null);
        setForm(emptyForm);
    };

    const isFormValid = () => {
        const amount = parseFloat(form.amount);
        return form.debitLineNr !== null
            && form.creditLineNr !== null
            && form.debitLineNr !== form.creditLineNr
            && !isNaN(amount) && amount > 0
            && form.reason.trim().length > 0
            && form.reason.trim().length <= 500;
    };

    const handleSave = async () => {
        if (!isFormValid()) return;
        setSaving(true);

        try {
            const amount = parseFloat(form.amount);
            if (isAdding) {
                await financialStatementApi.createReposting({
                    companyCode: company,
                    year,
                    debitLineNr: form.debitLineNr!,
                    creditLineNr: form.creditLineNr!,
                    amount,
                    reason: form.reason.trim(),
                });
                toast('Reposting created', 'success');
            } else if (editingId !== null) {
                await financialStatementApi.updateReposting(editingId, {
                    debitLineNr: form.debitLineNr!,
                    creditLineNr: form.creditLineNr!,
                    amount,
                    reason: form.reason.trim(),
                });
                toast('Reposting updated', 'success');
            }
            cancelForm();
            await loadData();
            onRepostingsChanged();
        } catch {
            toast('Failed to save reposting', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await financialStatementApi.deleteReposting(deleteTarget.RepostingId);
            toast('Reposting deleted', 'success');
            setDeleteTarget(null);
            await loadData();
            onRepostingsChanged();
        } catch {
            toast('Failed to delete reposting', 'error');
        }
    };

    const getLineName = (lineNr: number) => {
        const line = lines.find(l => l.LineNr === lineNr);
        return line ? (line.LineNameEnglish || line.LineNameGerman) : `Line ${lineNr}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
        );
    }

    const renderLineSelector = (
        value: number | null,
        onChange: (nr: number | null) => void,
        search: string,
        onSearchChange: (s: string) => void,
        filteredLines: LineConfig[],
        label: string,
    ) => (
        <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">{label}</label>
            <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search line..."
                    value={search}
                    onChange={e => onSearchChange(e.target.value)}
                    className="w-full pl-7 pr-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
            <div className="border rounded max-h-40 overflow-y-auto">
                {filteredLines.map(l => (
                    <div
                        key={l.LineNr}
                        onClick={() => onChange(l.LineNr)}
                        className={`px-2 py-1 text-sm cursor-pointer ${
                            value === l.LineNr
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-blue-50'
                        }`}
                    >
                        {l.LineNr} - {l.LineNameEnglish || l.LineNameGerman}
                    </div>
                ))}
                {filteredLines.length === 0 && (
                    <div className="px-2 py-2 text-sm text-gray-400 italic">No lines found</div>
                )}
            </div>
            {value !== null && (
                <div className="text-xs text-blue-600">
                    Selected: {value} - {getLineName(value)}
                </div>
            )}
        </div>
    );

    const renderForm = () => (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">
                {isAdding ? 'New Reposting' : 'Edit Reposting'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
                {renderLineSelector(
                    form.debitLineNr, nr => setForm(f => ({ ...f, debitLineNr: nr })),
                    debitSearch, setDebitSearch, filteredDebitLines,
                    'Debit Line (receives amount)',
                )}
                {renderLineSelector(
                    form.creditLineNr, nr => setForm(f => ({ ...f, creditLineNr: nr })),
                    creditSearch, setCreditSearch, filteredCreditLines,
                    'Credit Line (gives amount)',
                )}
            </div>
            {form.debitLineNr !== null && form.creditLineNr !== null && form.debitLineNr === form.creditLineNr && (
                <p className="text-xs text-red-600">Debit and credit lines must be different.</p>
            )}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600">Amount (CHF)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                        className="w-full text-sm border rounded px-2 py-1.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600">
                        Reason <span className="text-gray-400">({form.reason.trim().length}/500)</span>
                    </label>
                    <textarea
                        placeholder="Mandatory reason for this reposting..."
                        value={form.reason}
                        onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                        maxLength={500}
                        rows={2}
                        className="w-full text-sm border rounded px-2 py-1.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button
                    onClick={handleSave}
                    disabled={!isFormValid() || saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Check className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={cancelForm}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border rounded hover:bg-gray-50"
                >
                    <X className="h-4 w-4" />
                    Cancel
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                        Repostings for {year}
                    </h3>
                    <p className="text-xs text-gray-500">
                        Manual line-level adjustments with double-entry bookkeeping
                    </p>
                </div>
                {canEdit && !isAdding && editingId === null && (
                    <button
                        onClick={startAdd}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                    >
                        <Plus className="h-4 w-4" />
                        Add Reposting
                    </button>
                )}
            </div>

            {/* Add/Edit form */}
            {(isAdding || editingId !== null) && renderForm()}

            {/* Table */}
            {repostings.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                    No repostings for {year}. {canEdit && 'Click "Add Reposting" to create one.'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <th className="px-3 py-2">Debit Line</th>
                                <th className="px-3 py-2">Credit Line</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2">Reason</th>
                                <th className="px-3 py-2">Created By</th>
                                <th className="px-3 py-2">Date</th>
                                {canEdit && <th className="px-3 py-2 text-center">Actions</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {repostings.map(r => (
                                <tr key={r.RepostingId} className="hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                        <span className="text-gray-400 mr-1">{r.DebitLineNr}</span>
                                        {r.DebitLineName}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="text-gray-400 mr-1">{r.CreditLineNr}</span>
                                        {r.CreditLineName}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium">
                                        {formatAmount(r.Amount)}
                                    </td>
                                    <td className="px-3 py-2 max-w-xs truncate" title={r.Reason}>
                                        {r.Reason}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                                        {r.CreatedBy.split('@')[0]}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                                        {formatDate(r.CreatedAt)}
                                    </td>
                                    {canEdit && (
                                        <td className="px-3 py-2 text-center whitespace-nowrap">
                                            <button
                                                onClick={() => startEdit(r)}
                                                className="p-1 text-gray-400 hover:text-blue-600"
                                                title="Edit"
                                                disabled={isAdding || editingId !== null}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(r)}
                                                className="p-1 text-gray-400 hover:text-red-600 ml-1"
                                                title="Delete"
                                                disabled={isAdding || editingId !== null}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Total summary */}
            {repostings.length > 0 && (
                <div className="text-xs text-gray-500 text-right">
                    {repostings.length} reposting{repostings.length !== 1 ? 's' : ''} | Total amount: {formatAmount(repostings.reduce((sum, r) => sum + r.Amount, 0))} CHF
                </div>
            )}

            {/* Delete confirmation */}
            {deleteTarget && (
                <ConfirmModal
                    title="Delete Reposting"
                    message={`Delete reposting of ${formatAmount(deleteTarget.Amount)} CHF from "${deleteTarget.DebitLineName}" to "${deleteTarget.CreditLineName}"?`}
                    confirmLabel="Delete"
                    cancelLabel="Cancel"
                    variant="danger"
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}
